import { Link, Outlet, useNavigate } from "@tanstack/react-router";
import {
  ArrowLeft,
  Banknote,
  Building2,
  FileSpreadsheet,
  MapPin,
  Pencil,
  ShoppingCart,
  StickyNote,
  Users,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useVendor, useVendorCounts } from "@/features/vendors/api";
import { VENDOR_TYPES, formatMoney, labelFor } from "@/features/vendors/constants";
import { useVendorId } from "@/features/vendors/detail/use-vendor-id";
import { cn } from "@/lib/utils";

type CountKey = "contacts" | "addresses" | "notes";

const NAV = [
  { key: "profile", label: "Profile", to: "/vendors/$vendorId/profile", icon: Building2, live: true },
  {
    key: "contacts",
    label: "Contacts",
    to: "/vendors/$vendorId/contacts",
    icon: Users,
    live: true,
    countKey: "contacts" as const,
  },
  {
    key: "addresses",
    label: "Addresses",
    to: "/vendors/$vendorId/addresses",
    icon: MapPin,
    live: true,
    countKey: "addresses" as const,
  },
  {
    key: "notes",
    label: "Notes",
    to: "/vendors/$vendorId/notes",
    icon: StickyNote,
    live: true,
    countKey: "notes" as const,
  },
  {
    key: "ledger",
    label: "Ledger",
    to: "/vendors/$vendorId/ledger",
    icon: FileSpreadsheet,
    live: true,
  },
  {
    key: "orders",
    label: "Purchase orders",
    to: "/vendors/$vendorId/orders",
    icon: ShoppingCart,
    live: true,
  },
  {
    key: "payments",
    label: "Payments",
    to: "/vendors/$vendorId/payments",
    icon: Banknote,
    live: true,
  },
] as const;

export function VendorDetailLayout() {
  const vendorId = useVendorId();
  const navigate = useNavigate();
  const { data: vendor, isLoading, error } = useVendor(vendorId);
  const { data: counts } = useVendorCounts(vendorId);

  if (isLoading) {
    return (
      <div className="flex h-[calc(100vh-3.5rem)] items-center justify-center text-sm text-muted-foreground">
        Loading vendor…
      </div>
    );
  }

  if (error || !vendor) {
    return (
      <div className="flex h-[calc(100vh-3.5rem)] flex-col items-center justify-center gap-3">
        <p className="text-sm text-destructive">
          {(error as Error)?.message ?? "Vendor not found"}
        </p>
        <Button type="button" variant="outline" size="sm" onClick={() => void navigate({ to: "/vendors" })}>
          Back to vendors
        </Button>
      </div>
    );
  }

  return (
    <div className="-mx-4 -my-3 flex h-[calc(100vh-3rem)] min-h-0 flex-col bg-surface md:-mx-6 md:-my-4">
      <header className="flex shrink-0 items-center justify-between gap-3 border-b border-border bg-card px-4 py-2.5">
        <div className="flex min-w-0 items-center gap-2.5">
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            aria-label="Back to vendors"
            onClick={() => void navigate({ to: "/vendors" })}
          >
            <ArrowLeft />
          </Button>
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="truncate text-sm font-semibold tracking-[-0.01em]">
                <span className="font-mono text-muted-foreground">#{vendor.code}</span>
                <span className="mx-1.5 text-muted-foreground/40">·</span>
                {vendor.name}
              </h1>
              <Badge variant={vendor.is_active ? "success" : "secondary"}>
                {vendor.is_active ? "Active" : "Inactive"}
              </Badge>
              <Badge variant="outline">{labelFor(VENDOR_TYPES, vendor.vendor_type)}</Badge>
            </div>
            <p className="truncate text-2xs text-muted-foreground">
              {[vendor.city, vendor.state].filter(Boolean).join(", ")}
              {vendor.gstin ? ` · ${vendor.gstin}` : ""}
              {` · Batch ${vendor.batch_prefix}`}
              {vendor.outstanding_balance > 0
                ? ` · To pay ${formatMoney(vendor.outstanding_balance)}`
                : ""}
            </p>
          </div>
        </div>

        <div className="flex shrink-0 items-center gap-1.5">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => void navigate({ to: "/vendors/$vendorId/edit", params: { vendorId } })}
          >
            <Pencil />
            Edit
          </Button>
        </div>
      </header>

      <div className="flex min-h-0 flex-1">
        <aside className="flex w-48 shrink-0 flex-col border-r border-border bg-card">
          <ScrollArea className="flex-1 py-2">
            <nav className="space-y-px px-1.5">
              {NAV.map((item) => {
                const Icon = item.icon;
                const count =
                  "countKey" in item && counts ? counts[item.countKey as CountKey] : undefined;
                return (
                  <Link
                    key={item.key}
                    to={item.to}
                    params={{ vendorId }}
                    className={cn(
                      "flex items-center gap-2 rounded-md px-2 py-1.5 text-xs transition-colors",
                      item.live
                        ? "text-muted-foreground hover:bg-muted hover:text-foreground"
                        : "cursor-default text-muted-foreground/40",
                    )}
                    activeProps={{
                      className:
                        "bg-primary/10 font-medium text-primary hover:bg-primary/10 hover:text-primary",
                    }}
                    onClick={(e) => {
                      if (!item.live) e.preventDefault();
                    }}
                  >
                    <Icon className="size-3.5 shrink-0" />
                    <span className="min-w-0 flex-1 truncate">{item.label}</span>
                    {typeof count === "number" && count > 0 ? (
                      <span className="rounded bg-primary/15 px-1.5 text-[10px] font-medium tabular-nums text-primary">
                        {count}
                      </span>
                    ) : null}
                    {!item.live ? (
                      <span className="text-[9px] uppercase tracking-wide opacity-70">Soon</span>
                    ) : null}
                  </Link>
                );
              })}
            </nav>
          </ScrollArea>
        </aside>

        <div className="min-w-0 flex-1 overflow-auto p-3 md:p-4">
          <div className="min-h-full rounded-lg border border-border bg-card shadow-[0_1px_2px_rgba(15,23,42,0.04)]">
            <Outlet />
          </div>
        </div>
      </div>
    </div>
  );
}
